import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import Userfront from '../../auth/userfrontCompat';
import { FaPlus, FaSave, FaTrash } from 'react-icons/fa';
import './LeadOrganizationPanel.css';

export default function LeadOrganizationPanel({ leadId }) {
  const [lead, setLead] = useState(null);
  const [leadLists, setLeadLists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#4f46e5');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    const email = Userfront.user.email;
    const [leadR, listsR, labelsR] = await Promise.all([
      api.get(`/leads/lead/${leadId}`),
      api.get(`/leads/lead-lists?user=${encodeURIComponent(email)}`),
      api.get(`/leads/labels?user=${encodeURIComponent(email)}`),
    ]);
    setLead(leadR.data || null);
    setLeadLists(listsR.data || []);
    setLabels(labelsR.data || []);
    setSelectedLists(leadR.data?.leadLists || []);
    setSelectedLabels(leadR.data?.labels || []);
  };

  useEffect(() => { load().catch(() => setMessage('Kon categorisering niet laden.')); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const listIds = useMemo(() => new Set(selectedLists.map((item) => String(item.id))), [selectedLists]);
  const labelNames = useMemo(() => new Set(selectedLabels), [selectedLabels]);
  const dirty = useMemo(() => {
    if (!lead) return false;
    const currentLists = (lead.leadLists || []).map((x) => String(x.id)).sort().join('|');
    const nextLists = selectedLists.map((x) => String(x.id)).sort().join('|');
    const currentLabels = [...(lead.labels || [])].sort().join('|');
    const nextLabels = [...selectedLabels].sort().join('|');
    return currentLists !== nextLists || currentLabels !== nextLabels;
  }, [lead, selectedLists, selectedLabels]);

  const toggleList = (list) => {
    const id = String(list._id);
    setSelectedLists((current) => current.some((x) => String(x.id) === id)
      ? current.filter((x) => String(x.id) !== id)
      : [...current, { id, title: list.leadListTitle }]);
  };

  const toggleLabel = (name) => {
    setSelectedLabels((current) => current.includes(name) ? current.filter((x) => x !== name) : [...current, name]);
  };

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      await api.put(`/leads/lead/${leadId}`, { leadLists: selectedLists, labels: selectedLabels });
      setLead((current) => ({ ...current, leadLists: selectedLists, labels: selectedLabels }));
      setMessage('Categorisering opgeslagen.');
      window.setTimeout(() => window.location.reload(), 350);
    } catch (error) { setMessage(error.response?.data?.error || 'Opslaan mislukt.'); }
    finally { setSaving(false); }
  };

  const createLabel = async () => {
    const name = newLabel.trim(); if (!name) return;
    try {
      const result = await api.post('/leads/labels', { name, user: Userfront.user.email, color: newColor });
      setLabels((current) => [...current, result.data]);
      setSelectedLabels((current) => current.includes(result.data.name) ? current : [...current, result.data.name]);
      setNewLabel('');
    } catch (error) { setMessage(error.response?.data?.error || 'Label aanmaken mislukt.'); }
  };

  const deleteLabel = async (label) => {
    if (!window.confirm(`Label “${label.name}” verwijderen?`)) return;
    try {
      await api.delete(`/leads/labels/${label._id}`);
      setLabels((current) => current.filter((x) => x._id !== label._id));
      setSelectedLabels((current) => current.filter((x) => x !== label.name));
    } catch (error) { setMessage(error.response?.data?.error || 'Label verwijderen mislukt.'); }
  };

  return <section className="lead-org-panel card">
    <div className="card-head">
      <div><h2>Organisatie & categorisering</h2><p>Beheer leadlijsten en labels zonder de relatie- en CRM-intelligence uit het oog te verliezen.</p></div>
      <button disabled={!dirty || saving} onClick={save}><FaSave /> {saving ? 'Opslaan…' : 'Opslaan'}</button>
    </div>
    {message && <div className="lead-org-message">{message}</div>}
    <div className="lead-org-columns">
      <div>
        <div className="lead-org-heading"><strong>Leadlijsten</strong><span>{selectedLists.length} geselecteerd</span></div>
        <div className="lead-org-options">
          {leadLists.map((list) => <label key={String(list._id)} className={listIds.has(String(list._id)) ? 'selected' : ''}>
            <input type="checkbox" checked={listIds.has(String(list._id))} onChange={() => toggleList(list)} />
            <span>{list.leadListTitle}</span>
          </label>)}
          {!leadLists.length && <p className="muted">Nog geen leadlijsten beschikbaar.</p>}
        </div>
      </div>
      <div>
        <div className="lead-org-heading"><strong>Labels</strong><span>{selectedLabels.length} geselecteerd</span></div>
        <div className="lead-org-options label-options">
          {labels.map((label) => <div className={`lead-org-label-row ${labelNames.has(label.name) ? 'selected' : ''}`} key={String(label._id)}>
            <label><input type="checkbox" checked={labelNames.has(label.name)} onChange={() => toggleLabel(label.name)} /><i style={{ backgroundColor: label.color || '#9ca3af' }} /><span>{label.name}</span></label>
            <button type="button" title="Label verwijderen" onClick={() => deleteLabel(label)}><FaTrash /></button>
          </div>)}
        </div>
        <div className="lead-org-create"><input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Nieuw label" /><input aria-label="Labelkleur" type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} /><button type="button" onClick={createLabel}><FaPlus /> Toevoegen</button></div>
      </div>
    </div>
  </section>;
}
